import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import Cookies from 'js-cookie';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';

import useAuthenticatedRequest from '../../utils/TokenRefreshUtils';
import { formatShortDate } from '../../utils/CommonUtils';
import Drawer from '../common/Drawer';
import EmptyState from '../common/EmptyState';

const PAGE_SIZE = 25;

/**
 * Вибір клієнта для акту продажу + створення нового клієнта.
 *
 * Було: DataTable у панелі, де пошук фільтрував лише поточну сторінку
 * з десяти записів — тобто клієнта, якого не видно на екрані, знайти
 * було неможливо. Тепер довідник завантажується один раз і фільтрується
 * повністю, а сторінки гортаються локально.
 *
 * TODO(backend): серверний пошук у cagents/get_clients/ (параметр `search`),
 * щоб не тягнути весь довідник, коли клієнтів стане більше кількох сотень.
 */
const ClientPicker = ({ visible, onHide, selected, onSelect }) => {
    const authenticatedRequest = useAuthenticatedRequest();

    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [creating, setCreating] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!visible) {
            return;
        }

        const fetchClients = async () => {
            setLoading(true);

            const url = `${process.env.REACT_APP_BACKEND_URL}cagents/get_clients/?page=1&page_size=500&ordering=name`;
            const response = await authenticatedRequest(url, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (response?.status === 200) {
                const parsed = await response.json();
                setClients(parsed.results ?? []);
            }

            setLoading(false);
        };

        fetchClients();
    }, [authenticatedRequest, visible, reloadToken]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();

        if (!term) {
            return clients;
        }

        return clients.filter((client) => client.name?.toLowerCase().includes(term));
    }, [clients, search]);

    const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

    useEffect(() => {
        setPage(1);
    }, [search]);

    const newClientFormik = useFormik({
        initialValues: {
            type: 'INDIVIDUAL',
            phone: '',
            email: '',
            last_name: '',
            first_name: '',
            surname: '',
            date_of_birth: null,
            long_name: '',
            short_name: '',
            representative: '',
            edrpou: '',
        },
        validate: (values) => {
            const errors = {};

            if (values.type === 'INDIVIDUAL') {
                if (!values.last_name.trim()) {
                    errors.last_name = 'Вкажіть прізвище';
                }

                if (!values.first_name.trim()) {
                    errors.first_name = 'Вкажіть ім’я';
                }
            } else {
                if (!values.long_name.trim()) {
                    errors.long_name = 'Вкажіть повну назву';
                }

                if (!values.short_name.trim()) {
                    errors.short_name = 'Вкажіть скорочену назву';
                }

                if (!values.edrpou.trim()) {
                    errors.edrpou = 'Вкажіть ЄДРПОУ';
                } else if (!/^\d{8,10}$/.test(values.edrpou.trim())) {
                    errors.edrpou = 'ЄДРПОУ — це 8–10 цифр';
                }
            }

            return errors;
        },
        onSubmit: async (values) => {
            const payload = {
                type: values.type,
                division: Cookies.get('cashAppDivision'),
                phone: values.phone,
                email: values.email,
                is_partner: false,
                is_client: true,
                // TODO(backend): країна й область зашиті в застосунку.
                // Правильніше брати їх із підрозділу або дати довідник.
                country: 'UKRAINE',
                region: 'LVIV_REGION',
                city: '',
                street: '',
                building_number: '',
                apartment_number: '',
                notes: '',
                cagent_financial_data: [],
                ...(values.type === 'INDIVIDUAL'
                    ? {
                          individual_cagent_data: {
                              first_name: values.first_name,
                              last_name: values.last_name,
                              surname: values.surname,
                              date_of_birth: values.date_of_birth
                                  ? formatShortDate(values.date_of_birth)
                                  : null,
                          },
                      }
                    : {
                          legal_cagent_data: {
                              long_name: values.long_name,
                              short_name: values.short_name,
                              representative: values.representative,
                              edrpou: values.edrpou,
                          },
                      }),
            };

            const response = await authenticatedRequest(
                `${process.env.REACT_APP_BACKEND_URL}cagents/create/`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }
            );

            if (response?.status === 201) {
                const created = await response.json();
                newClientFormik.resetForm();
                setCreating(false);
                setReloadToken((token) => token + 1);

                // Одразу підставляємо створеного клієнта в акт, але лише
                // якщо бекенд повернув і id, і відображуване ім'я.
                if (created?.id && created?.name) {
                    onSelect(created);
                }
            }
        },
    });

    const error = (name) => newClientFormik.touched[name] && newClientFormik.errors[name];

    const field = (name, label, props = {}) => (
        <div className="rc-field">
            <label className="rc-field__label" htmlFor={name}>
                {label}
            </label>
            <InputText
                id={name}
                name={name}
                value={newClientFormik.values[name]}
                onChange={newClientFormik.handleChange}
                onBlur={newClientFormik.handleBlur}
                className={error(name) ? 'p-invalid' : ''}
                {...props}
            />
            {error(name) && <small className="rc-field-error">{newClientFormik.errors[name]}</small>}
        </div>
    );

    if (creating) {
        return (
            <Drawer
                visible={visible}
                onHide={() => {
                    setCreating(false);
                    onHide();
                }}
                title="Новий клієнт"
                footer={
                    <>
                        <Button
                            label="Створити клієнта"
                            icon="pi pi-check"
                            className="w-full"
                            onClick={newClientFormik.handleSubmit}
                        />
                        <Button
                            label="Назад до списку"
                            severity="secondary"
                            text
                            className="w-full"
                            onClick={() => setCreating(false)}
                        />
                    </>
                }
            >
                <form onSubmit={newClientFormik.handleSubmit}>
                    <div className="rc-field">
                        <SelectButton
                            value={newClientFormik.values.type}
                            options={[
                                { label: 'Фізична особа', value: 'INDIVIDUAL' },
                                { label: 'Юридична особа', value: 'LEGAL' },
                            ]}
                            onChange={(e) =>
                                e.value && newClientFormik.setFieldValue('type', e.value)
                            }
                            allowEmpty={false}
                        />
                    </div>

                    {newClientFormik.values.type === 'INDIVIDUAL' ? (
                        <>
                            {field('last_name', 'Прізвище')}
                            {field('first_name', 'Ім’я')}
                            {field('surname', 'По батькові')}
                            <div className="rc-field">
                                <label className="rc-field__label" htmlFor="date_of_birth">
                                    Дата народження
                                </label>
                                <Calendar
                                    inputId="date_of_birth"
                                    value={newClientFormik.values.date_of_birth}
                                    onChange={(e) =>
                                        newClientFormik.setFieldValue('date_of_birth', e.value)
                                    }
                                    dateFormat="dd.mm.yy"
                                    showIcon
                                    locale="ua"
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {field('long_name', 'Повна назва')}
                            {field('short_name', 'Скорочена назва')}
                            {field('representative', 'Представник')}
                            {field('edrpou', 'ЄДРПОУ', { inputMode: 'numeric' })}
                        </>
                    )}

                    {field('phone', 'Телефон', { inputMode: 'tel', autoComplete: 'tel' })}
                    {field('email', 'Email', { type: 'email', autoComplete: 'email' })}
                </form>
            </Drawer>
        );
    }

    return (
        <Drawer
            visible={visible}
            onHide={onHide}
            title="Клієнт"
            flush
            footer={
                <>
                    <Button
                        label="Новий клієнт"
                        icon="pi pi-user-plus"
                        severity="secondary"
                        outlined
                        className="w-full"
                        onClick={() => setCreating(true)}
                    />
                    {selected && (
                        <Button
                            label="Продати без клієнта"
                            icon="pi pi-times"
                            severity="secondary"
                            text
                            className="w-full"
                            onClick={() => onSelect(null)}
                        />
                    )}
                </>
            }
        >
            <div style={{ padding: 'var(--rc-space-3)' }}>
                <IconField iconPosition="left" className="w-full">
                    <InputIcon className="pi pi-search" />
                    <InputText
                        className="w-full"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Пошук клієнта"
                        aria-label="Пошук клієнта"
                    />
                </IconField>
            </div>

            {loading ? (
                <EmptyState icon="pi pi-spin pi-spinner" text="Завантаження довідника…" />
            ) : pageItems.length === 0 ? (
                <EmptyState
                    icon="pi pi-user"
                    title="Клієнтів не знайдено"
                    text={search ? `За запитом «${search}» нічого немає` : 'Довідник порожній'}
                />
            ) : (
                <>
                    <ul className="rc-list">
                        {pageItems.map((client) => (
                            <li key={client.id}>
                                <button
                                    type="button"
                                    className={`rc-list-item ${
                                        selected?.id === client.id ? 'rc-list-item--active' : ''
                                    }`.trim()}
                                    onClick={() => onSelect(client)}
                                >
                                    <div className="rc-list-item__main">
                                        <div className="rc-list-item__title">{client.name}</div>
                                        {client.phone && (
                                            <div className="rc-list-item__meta">
                                                <i className="pi pi-phone" />
                                                {client.phone}
                                            </div>
                                        )}
                                    </div>
                                    {selected?.id === client.id && <i className="pi pi-check" />}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {totalPages > 1 && (
                        <div className="rc-list-pager">
                            <Button
                                icon="pi pi-chevron-left"
                                text
                                size="small"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                            />
                            <span>
                                {page} / {totalPages} · знайдено {filtered.length}
                            </span>
                            <Button
                                icon="pi pi-chevron-right"
                                text
                                size="small"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                            />
                        </div>
                    )}
                </>
            )}
        </Drawer>
    );
};

export default ClientPicker;
